import React, { useState, useEffect, useRef } from 'react';

interface AboutUsSectionProps {
  subtitle: string;
  title: string;
  description: string;
  bulletPoints?: Array<{text: string; url: string}>;
  exploreMoreLink: string;
  contactNumber: string;
  images: {
    main?: string;
    secondary?: string;
    borderShape?: string;
    bus?: string;
    girl?: string;
    dot?: string;
  };
}

interface WazeStatusResponse {
  success: boolean;
  status: string;
  message: string;
  data?: {
    wazeStatus?: string;
    eta?: number;
    lat?: number;
    lng?: number;
    routeTimestamp?: number;
    driverName?: string;
    speedKmh?: number;
  };
  linkInfo?: {
    url: string;
    createdAt: string;
  };
}

const KidsaAboutUs: React.FC<AboutUsSectionProps> = ({
  title,
  subtitle,
  description,
  bulletPoints = [],
  exploreMoreLink,
  contactNumber,
  images,
}) => {
  const [wazeActive, setWazeActive] = useState(false);
  const [wazeData, setWazeData] = useState<WazeStatusResponse | null>(null);
  const [wazeUrl, setWazeUrl] = useState<string | null>(null);

  useEffect(() => {
    const checkEndpoint = async () => {
      try {
        // 🆕 Nuevo endpoint público de drivers
        const response = await fetch('https://backend.anubisai.net/api/whatsapp/wasp/public/bus/status');
        const data: WazeStatusResponse = await response.json();
        
        console.log('[Bus Status]', data); // Debug
        
        setWazeData(data);
        
        if (data.success && data.status === 'active') {
          const isActive = data.data?.wazeStatus === "ok";
          setWazeActive(isActive);
          
          if (isActive && data.linkInfo?.url) {
            setWazeUrl(data.linkInfo.url);
          } else {
            setWazeUrl(null);
          }
        } else {
          setWazeActive(false);
          setWazeUrl(null);
          console.log('Bus inactivo:', data.message);
        }
        
      } catch (error) {
        console.error('Error al consultar estado del bus:', error);
        setWazeActive(false);
        setWazeUrl(null);
      }
    };
    
    // Ejecutar inmediatamente
    checkEndpoint();
    
    // Actualizar cada 30 segundos
    const intervalId = setInterval(checkEndpoint, 30000);
    
    return () => clearInterval(intervalId);
  }, []);

  return (
    <section className="about-section section-padding" id="nosotros">
      <div className="bus-shape float-bob-x">
        <img src={images.bus || "/assets/kidsa/assets/img/about/bus.png"} alt="shape-img" />
      </div>
      <div className="girl-shape float-bob-y">
        <img src={images.girl || "/assets/kidsa/assets/img/about/backpack.png"} alt="shape-img" />
      </div>
      <div className="dot-shape">
        <img src={images.dot || "/assets/kidsa/assets/img/about/dot.png"} alt="shape-img" />
      </div>
      <div className="container">
        <div className="about-wrapper mb-40">
          <div className="row g-4">
            <div className="col-lg-6">
              <div className="about-image-items">
                <div className="about-image wow fadeInUp" data-wow-delay=".3s">
                  <img src={images.main || "/assets/kidsa/assets/img/about/02.jpg"} alt="about-img" />
                </div>
                <div className="about-image-2">
                  <img src={images.secondary || "/assets/kidsa/assets/img/about/02.png"} alt="about-img" />
                </div>
                <div className="border-shape-1">
                  <img src={images.borderShape || "/assets/kidsa/assets/img/about/border-shape-1.png"} alt="img" />
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="about-content">
                <div className="section-title">
                  <span className="wow fadeInUp">{subtitle}</span>
                  <h2 className="wow fadeInUp hero-content" data-wow-delay=".3s">{title}</h2>
                </div>
                <p className="mt-3 mt-md-0 wow fadeInUp" data-wow-delay=".5s">
                  {description}
                </p>
                <div className="about-list">
                  <ul className="wow fadeInUp" data-wow-delay=".3s">
                    {(bulletPoints.length > 0 ? bulletPoints.slice(0, 4) : [
                      {text: "1", url: "#"}, 
                      {text: "2", url: "#"},
                      {text: "3", url: "#"},
                      {text: "4", url: "#"}
                    ]).map((point, index) => (
                      <li key={index}>
                        <i className="fa-regular fa-circle-check"></i>
                        <a href={point.url}>{point.text}</a>
                      </li>
                    ))}
                  </ul>
                  <ul className="wow fadeInUp" data-wow-delay=".5s">
                    {(bulletPoints.length > 4 ? bulletPoints.slice(4) : [
                      {text: "5", url: "#"}, 
                      {text: "6", url: "#"},
                      {text: "7", url: "#"},
                      {text: "8", url: "#"}
                    ]).map((point, index) => (
                      <li key={index}>
                        <i className="fa-regular fa-circle-check"></i>
                        <a href={point.url}>{point.text}</a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="about-author">
                  <div className="about-button wow fadeInUp" data-wow-delay=".3s">
                    <div className={`schoolbus ${!wazeActive ? 'disabled' : 'enabled'}`}>
                      <div className="semaforo wow mt-5 py-1 px-1">
                        {/* 🔴 Luz roja cuando NO está activo */}
                        <div 
                          className={`status-indicator me-2 bg-danger`} 
                          style={{ 
                            width: '10px', 
                            height: '10px', 
                            borderRadius: '50%',
                            opacity: wazeActive ? 0.3 : 1
                          }}
                        />
                        {/* 🟢 Luz verde cuando SÍ está activo */}
                        <div 
                          className={`status-indicator me-2 mt-1 bg-success`} 
                          style={{ 
                            width: '10px', 
                            height: '10px', 
                            borderRadius: '50%',
                            opacity: wazeActive ? 1 : 0.3
                          }}
                        />
                      </div>
                      {wazeActive && wazeUrl ? (
                        <a 
                          href={wazeUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          title="Seguir bus en Waze"
                        >
                          {/* Icono del bus */}
                        </a>
                      ) : (
                        <a className="disabled">{/*N/A*/}</a>
                      )}
                    </div>
                  </div>
                  <div className="author-icon wow fadeInUp" data-wow-delay=".5s">
                    <div className="icon">
                      <i className="fa-solid fa-phone"></i>
                    </div>
                    <div className="hero-content">
                      <span>WhatsApp</span>
                      <h5>
                        <a href={`tel:${(contactNumber || '').replace(/\s+/g, '')}`}>
                          {contactNumber}
                        </a>
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KidsaAboutUs;